// NotaAnota Service for operational panel data
const API_BASE_URL = import.meta.env.VITE_API_URL 

class NotaAnotaService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  // Helper method to check if token is expired
  isTokenExpired(token) {
    if (!token) return true
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      return true
    }
  }

  // Get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken')
    
    if (token && !this.isTokenExpired(token)) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    } else if (token) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      window.dispatchEvent(new CustomEvent('auth-token-invalid'))
    }
    
    return {
      'Content-Type': 'application/json'
    }
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    
    try {
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
        ...options
      })

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken')
          localStorage.removeItem('user')
          window.dispatchEvent(new CustomEvent('auth-token-invalid'))
          const err = new Error('Token inválido ou expirado')
          err.name = 'UnauthorizedError'
          err.status = 401
          throw err
        }
        const bodyText = await response.text()
        let message = ''
        try {
          const parsed = JSON.parse(bodyText)
          message = parsed?.message || ''
        } catch {}
        const finalMsg = message && String(message).trim() !== '' ? message : `HTTP error! status: ${response.status}`
        throw new Error(finalMsg)
      }

      return await response.json()
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  // Get method
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET'
    })
  }

  // Get onboard data by day and company
  async getOnboardPorDia(data, empresa) {
    const params = new URLSearchParams({
      data: data,
      empresa: empresa
    })
    return this.get(`/v1/notaanota/onboard/dia?${params}`)
  }

  // Get vehicles data by day
  async getVeiculosPorDia(data, empresa) {
    const params = new URLSearchParams({
      data: data,
      empresa: empresa
    })
    return this.get(`/v1/notaanota/veiculos/dia?${params}`)
  }

  // Get clients data by day
  async getClientesPorDia(data, empresa) {
    const params = new URLSearchParams({
      data: data,
      empresa: empresa
    })
    return this.get(`/v1/notaanota/clientes/dia?${params}`)
  }

  async imprimir(payload) {
    return this.request('/v1/notaanota/imprimir', { method: 'POST', body: JSON.stringify(payload) })
  }

  async registrarReimpressao(payload) {
    return this.request('/v1/notaanota/reimpressao', { method: 'POST', body: JSON.stringify(payload) })
  }

  async getDadosReimpressao(chave, volume) {
    const params = new URLSearchParams({ chave, volume: String(volume) })
    return this.get(`/v1/notaanota/reimpressao/dados?${params.toString()}`)
  }

  async getReimpressaoPorDia(veiculoId, data) {
    const params = new URLSearchParams({ veiculoId, data })
    const endpoint = `/v1/notaanota/reimpressao/dia?${params.toString()}`
    const url = `${this.baseURL}${endpoint}`
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })
      if (response.status === 404) {
        return []
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async getSequence() {
    return this.get('/v1/notaanota/seq')
  }

  async setSequence(nr1) {
    return this.request('/v1/notaanota/seq', { method: 'PATCH', body: JSON.stringify({ nr1 }) })
  }
}

const notaAnotaService = new NotaAnotaService()

export default notaAnotaService
export const notaanota = notaAnotaService